import * as React from "react";
import { Box, Button, ButtonBase, IconButton, Stack, TextField, Typography } from "@mui/material";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";

import type { BriefAgentChoice } from "@/hooks/api/use-campaign-briefs";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { FilterOption } from "@/components/customTable/types";
import { FilterFetchSelect } from "@/components/fetch-select";

import { SectionCard, SELECTED_TINT } from "./section-card";

/** The API's ceiling; more than this is a conversation, not a form. */
export const MAX_EXTRA_AGENTS = 5;

const CHOICES: { value: BriefAgentChoice; label: string; hint: string; price: string }[] = [
	{
		value: "new",
		label: "Build a new agent",
		hint: "We build it from the call flow you attach below.",
		price: "Campaign setup fee",
	},
	{
		value: "existing",
		label: "Use one of your agents",
		hint: "Its call flow is already done.",
		price: "No setup fee",
	},
];

interface AgentsSectionProps {
	step: number;
	choice: BriefAgentChoice | null;
	onChoice: (choice: BriefAgentChoice) => void;
	agent: { id: string; name: string };
	onAgent: (agent: { id: string; name: string }) => void;
	extraAgents: string[];
	onExtraAgents: (agents: string[]) => void;
	/** "SGD 1,728", or null while the rate card loads. */
	extraAgentPrice: string | null;
	errors: Partial<Record<string, string>>;
}

/** The main agent, which setup covers, and any extra agents, each priced. */
export function AgentsSection({
	step,
	choice,
	onChoice,
	agent,
	onAgent,
	extraAgents,
	onExtraAgents,
	extraAgentPrice,
	errors,
}: AgentsSectionProps): React.JSX.Element {
	const addRef = React.useRef<HTMLInputElement>(null);
	const [focusNew, setFocusNew] = React.useState(false);

	React.useEffect(() => {
		if (!focusNew) return;
		addRef.current?.focus();
		setFocusNew(false);
	}, [focusNew]);

	return (
		<SectionCard
			id="agents"
			step={step}
			title="Agents"
			description="Your main agent comes with the campaign. Add another for a separate job, such as inbound callbacks or booking appointments."
		>
			<Box>
				<Box
					role="radiogroup"
					aria-label="Main agent"
					sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}
				>
					{CHOICES.map((option) => {
						const selected = choice === option.value;
						return (
							<ButtonBase
								key={option.value}
								role="radio"
								aria-checked={selected}
								onClick={() => onChoice(option.value)}
								sx={{
									display: "flex",
									alignItems: "flex-start",
									justifyContent: "flex-start",
									gap: 1.5,
									p: 2,
									textAlign: "left",
									borderRadius: 1.5,
									border: "1.5px solid",
									borderColor: selected
										? "primary.main"
										: errors.agentChoice
											? "error.main"
											: "var(--mui-palette-divider)",
									bgcolor: selected ? SELECTED_TINT : "transparent",
									"&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
								}}
							>
								<Box
									aria-hidden
									sx={{
										flex: "none",
										mt: 0.25,
										width: 20,
										height: 20,
										borderRadius: "50%",
										border: "2px solid",
										borderColor: selected ? "primary.main" : "var(--mui-palette-neutral-400)",
										bgcolor: selected ? "primary.main" : "transparent",
										boxShadow: selected ? "inset 0 0 0 3px var(--mui-palette-background-paper)" : "none",
									}}
								/>
								<Box>
									<Typography variant="subtitle2">{option.label}</Typography>
									<Typography variant="body2" color="text.secondary">
										{option.hint}
									</Typography>
									<Typography
										variant="caption"
										color={option.value === "existing" ? "success.main" : "text.secondary"}
										sx={{ fontWeight: 600 }}
									>
										{option.price}
									</Typography>
								</Box>
							</ButtonBase>
						);
					})}
				</Box>
				{errors.agentChoice ? <CustomFormHelperError text={errors.agentChoice} /> : null}
			</Box>

			{choice === "existing" ? (
				<Box>
					<FilterFetchSelect
						label="Which agent?"
						showInputLabel
						value={{ label: agent.name, value: agent.id } as FilterOption}
						placeholder="Select one of your agents"
						onChange={(selected: FilterOption | string) => {
							const option = typeof selected === "string" ? { value: selected, label: selected } : selected;
							onAgent({ id: (option.value as string) || "", name: option.label || "" });
						}}
						getOptionsUrl="/campaign-briefs/agents"
						fetchLabel="name"
						fetchValue="id"
					/>
					{errors.agentId ? <CustomFormHelperError text={errors.agentId} /> : null}
				</Box>
			) : null}

			{extraAgents.map((description, index) => (
				<Box key={index}>
					<Stack
						direction="row"
						spacing={1.5}
						sx={{
							alignItems: "center",
							p: 1.5,
							borderRadius: 1.5,
							border: "1px solid var(--mui-palette-divider)",
							bgcolor: "var(--mui-palette-background-level1)",
						}}
					>
						<TextField
							inputRef={index === extraAgents.length - 1 ? addRef : undefined}
							size="small"
							fullWidth
							label={`Extra agent ${index + 1}: what does it do?`}
							placeholder="Inbound callbacks"
							value={description}
							error={Boolean(errors[`extraAgents.${index}`])}
							onChange={(event) =>
								onExtraAgents(extraAgents.map((other, at) => (at === index ? event.target.value : other)))
							}
							sx={{ bgcolor: "background.paper" }}
						/>
						{extraAgentPrice ? (
							<Typography variant="subtitle2" color="primary.main" sx={{ whiteSpace: "nowrap" }}>
								+{extraAgentPrice}
							</Typography>
						) : null}
						<IconButton
							aria-label={`Remove extra agent ${index + 1}`}
							onClick={() => onExtraAgents(extraAgents.filter((_, at) => at !== index))}
						>
							<XIcon />
						</IconButton>
					</Stack>
					{errors[`extraAgents.${index}`] ? <CustomFormHelperError text={errors[`extraAgents.${index}`]!} /> : null}
				</Box>
			))}

			<Box>
				<Button
					variant="outlined"
					startIcon={<PlusIcon />}
					disabled={extraAgents.length >= MAX_EXTRA_AGENTS}
					onClick={() => {
						onExtraAgents([...extraAgents, ""]);
						setFocusNew(true);
					}}
					sx={{ borderStyle: "dashed" }}
				>
					Add another agent{extraAgentPrice ? ` · ${extraAgentPrice}` : ""}
				</Button>
				{extraAgents.length >= MAX_EXTRA_AGENTS ? (
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
						Need more than {MAX_EXTRA_AGENTS}? Tell us in the notes.
					</Typography>
				) : null}
			</Box>
		</SectionCard>
	);
}
